import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Label,
  Button,
  Form,
  Alert,
  FormGroup,
  Badge,
} from "reactstrap";
import { post } from "../../helpers/api_helper";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ADD_PARTNER_BRANCH_TIMESLOT, PARTNERS_BRANCH_SEARCH } from "helpers/api_endpoints";
import Select from "react-select";

document.title = "Add Partner Branch Time Slot";

const AddPartnerBranchTimeSlot = () => {
  const navigate = useNavigate();
  const [partnerBranchId, setPartnerBranchId] = useState(0)
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [partnerBranches, setPartnerBranches] = useState([])

  // Form state
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [cutOffTime, setCutOffTime] = useState("");
  const [orderStartTime, setOrderStartTime] = useState("");
  const [expectedOrders, setExpectedOrders] = useState(0);
  const [type, setType] = useState("regular");
  const [status, setStatus] = useState(1);
  
  // For regular time slots
  const [recurringDays, setRecurringDays] = useState([]);
  
  // For exceptional time slots
  const [dateRanges, setDateRanges] = useState([{ startDate: "", endDate: "" }]);

  // Day options for recurring days
  const dayOptions = [
    { value: 0, label: "Sunday" },
    { value: 1, label: "Monday" },
    { value: 2, label: "Tuesday" },
    { value: 3, label: "Wednesday" },
    { value: 4, label: "Thursday" },
    { value: 5, label: "Friday" },
    { value: 6, label: "Saturday" },
  ];

  // Helper function to convert time string to minutes since midnight
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours * 60 + minutes;
  };

  // Handle adding new date range
  const addDateRange = () => {
    setDateRanges([...dateRanges, { startDate: "", endDate: "" }]);
  };

  // Handle removing date range
  const removeDateRange = (index) => {
    if (dateRanges.length <= 1) return;
    const newRanges = [...dateRanges];
    newRanges.splice(index, 1);
    setDateRanges(newRanges);
  };

  // Handle date range change
  const handleDateRangeChange = (index, field, value) => {
    const newRanges = [...dateRanges];
    newRanges[index][field] = value;
    setDateRanges(newRanges);
  };

  const fetchPartners = async () => {
    setLoading(true)
    try {
      const response = await post(PARTNERS_BRANCH_SEARCH)
      const formattedPartners = response.data.map(el => ({
        value: el.id,
        label: el.name,
      }))
      console.log("formattedPartners", formattedPartners)
      setPartnerBranches(formattedPartners)
    } catch (error) {
      setErrorMsg(error?.response?.data?.message || "Failed to fetch countries")
    } finally {
      setLoading(false)
    }
  }

  // Validate form before submission
  const validateForm = () => {
    if (!startTime || !endTime || !cutOffTime) {
      setErrorMsg("Start time, end time and cut off time are required");
      return false;
    }

    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    const cutOffMinutes = timeToMinutes(cutOffTime);
    const orderStartMinutes = orderStartTime ? timeToMinutes(orderStartTime) : null;

    if (startMinutes >= endMinutes) {
      setErrorMsg("Start time must be before end time");
      return false;
    }

    if (cutOffMinutes > endMinutes) {
      setErrorMsg("Cut off time must not be later than end time");
      return false;
    }

    if (orderStartTime && orderStartMinutes >= cutOffMinutes) {
      setErrorMsg("Order start time must be before cut off time");
      return false;
    }

    if (isNaN(expectedOrders) || expectedOrders < 0) {
      setErrorMsg("Expected orders must be a positive number");
      return false;
    }

    if (type === "regular" && recurringDays.length === 0) {
      setErrorMsg("Regular time slots must have at least one recurring day");
      return false;
    }

    if (type === "exceptional") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      for (const range of dateRanges) {
        if (!range.startDate || !range.endDate) {
          setErrorMsg("All date ranges must have both start and end dates");
          return false;
        }

        const startDate = new Date(range.startDate);
        const endDate = new Date(range.endDate);

        if (startDate > endDate) {
          setErrorMsg("Start date must be before or equal to end date in date ranges");
          return false;
        }

        if (endDate < today) {
          setErrorMsg("Cannot create time slots for past dates");
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const timeSlotData = {
        partnerBranchId: Number(partnerBranchId),
        startTime: startTime + ":00",
        endTime: endTime + ":00",
        cutOffTime: cutOffTime + ":00",
        orderStartTime: orderStartTime ? orderStartTime + ":00" : null,
        expectedOrders: Number(expectedOrders),
        type,
        status: Number(status),
        recurringDays: type === "regular" ? recurringDays : null,
        dateRanges: type === "exceptional" ? dateRanges : null,
      };

      const response = await post(ADD_PARTNER_BRANCH_TIMESLOT, timeSlotData);

      if (response.code === 201) {
        navigate(`/partnersbranch/timeslot`, {
          state: { successMsg: response.message || "Time slot created successfully" },
        });
      }
    } catch (error) {
      setErrorMsg(
        error?.response?.data?.message || "Failed to create time slot"
      );
      console.error("Error creating time slot:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            title="Partner Branches"
            breadcrumbItem="Add Partner Branch Time Slot"
          />

          <Form onSubmit={handleSubmit}>
            <Row>
              <Col xl={12}>
                <Card>
                  <CardBody>
                    {errorMsg && <Alert color="danger">{errorMsg}</Alert>}

                    <Row>
                      <div className="form-group col-md-6 mt-2">
                        <Label className="form-label">
                          <Trans>Partner Branch</Trans>
                        </Label>
                        <Select
                          name="partnerId"
                          options={partnerBranches}
                          value={partnerBranches.filter(option => {
                            return partnerBranchId == option.value
                          })}
                          className="form-control select2"
                          onChange={e => setPartnerBranchId(e.value)}
                        />
                      </div>

                      <FormGroup className="col-md-6">
                        <Label>
                          <Trans>Type</Trans>
                        </Label>
                        <Input
                          type="select"
                          value={type}
                          onChange={e => setType(e.target.value)}
                          required
                        >
                          <option value="regular">Regular</option>
                          <option value="exceptional">Exceptional</option>
                        </Input>
                      </FormGroup>

                      <FormGroup className="col-md-6">
                        <Label>
                          <Trans>Status</Trans>
                        </Label>
                        <Input
                          type="select"
                          value={status}
                          onChange={e => setStatus(parseInt(e.target.value))}
                          required
                        >
                          <option value={1}>Active</option>
                          <option value={0}>Inactive</option>
                        </Input>
                      </FormGroup>

                      <FormGroup className="col-md-3">
                        <Label>
                          <Trans>Start Time</Trans>
                        </Label>
                        <Input
                          type="time"
                          value={startTime}
                          onChange={e => setStartTime(e.target.value)}
                          required
                        />
                      </FormGroup>

                      <FormGroup className="col-md-3">
                        <Label>
                          <Trans>End Time</Trans>
                        </Label>
                        <Input
                          type="time"
                          value={endTime}
                          onChange={e => setEndTime(e.target.value)}
                          required
                        />
                      </FormGroup>

                      <FormGroup className="col-md-3">
                        <Label>
                          <Trans>Cut Off Time</Trans>
                        </Label>
                        <Input
                          type="time"
                          value={cutOffTime}
                          onChange={e => setCutOffTime(e.target.value)}
                          required
                        />
                      </FormGroup>

                      <FormGroup className="col-md-3">
                        <Label>
                          <Trans>Order Start Time (Optional)</Trans>
                        </Label>
                        <Input
                          type="time"
                          value={orderStartTime}
                          onChange={e => setOrderStartTime(e.target.value)}
                        />
                      </FormGroup>

                      <FormGroup className="col-md-6">
                        <Label>
                          <Trans>Expected Orders</Trans>
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          value={expectedOrders}
                          onChange={e => setExpectedOrders(e.target.value)}
                          required
                        />
                      </FormGroup>

                      {type === "regular" && (
                        <FormGroup className="col-md-6">
                          <Label>
                            <Trans>Recurring Days</Trans>
                          </Label>
                          <Select
                            isMulti
                            options={dayOptions}
                            value={dayOptions.filter(option =>
                              recurringDays.includes(option.value)
                            )}
                            onChange={selected =>
                              setRecurringDays(selected.map(item => item.value))
                            }
                            className="basic-multi-select"
                            classNamePrefix="select"
                            required
                          />
                        </FormGroup>
                      )}

                      {type === "exceptional" && (
                        <FormGroup className="col-12">
                          <Label>
                            <Trans>Date Ranges</Trans>
                            <Button
                              color="link"
                              size="sm"
                              onClick={addDateRange}
                              className="float-end"
                            >
                              <i className="mdi mdi-plus"></i> Add Range
                            </Button>
                          </Label>
                          {dateRanges.map((range, index) => (
                            <div key={index} className="mb-3">
                              <Row>
                                <Col md={5}>
                                  <Input
                                    type="date"
                                    value={range.startDate}
                                    onChange={e =>
                                      handleDateRangeChange(
                                        index,
                                        "startDate",
                                        e.target.value
                                      )
                                    }
                                    required
                                  />
                                </Col>
                                <Col md={5}>
                                  <Input
                                    type="date"
                                    value={range.endDate}
                                    onChange={e =>
                                      handleDateRangeChange(
                                        index,
                                        "endDate",
                                        e.target.value
                                      )
                                    }
                                    required
                                  />
                                </Col>
                                <Col md={2}>
                                  {dateRanges.length > 1 && (
                                    <Button
                                      color="danger"
                                      outline
                                      onClick={() => removeDateRange(index)}
                                    >
                                      <i className="mdi mdi-delete"></i>
                                    </Button>
                                  )}
                                </Col>
                              </Row>
                            </div>
                          ))}
                        </FormGroup>
                      )}
                    </Row>

                    <div style={{ color: "red", fontWeight: "bold" }}>
                      For Regular Schedule Entities: Recurring
                      days will be add
                    </div>
                    <br />
                    <div style={{ color: "red", fontWeight: "bold" }}>
                      For Exceptional Schedule Entities: Date
                      ranges will be add, and These override any regular schedule defined for the same date.
                    </div>

                    <div className="mt-4">
                      <Button type="submit" color="primary" disabled={loading}>
                        {loading ? "Processing..." : "Submit"}
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
    </React.Fragment>
  )
};

export default AddPartnerBranchTimeSlot;